import { useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../api/client";
import type { FeedbackCategory } from "../types";

const CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: "bug", label: "Bug" },
  { value: "idea", label: "Idea" },
  { value: "question", label: "Question" },
];

interface Props {
  onClose: () => void;
  /** Pre-attaches a session to the report (session-report entry point). */
  sessionId?: number;
  initialCategory?: FeedbackCategory;
}

export default function FeedbackModal({ onClose, sessionId, initialCategory = "bug" }: Props) {
  const location = useLocation();
  const [category, setCategory] = useState<FeedbackCategory>(initialCategory);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!message.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.sendFeedback({
        category,
        message: message.trim(),
        route: location.pathname,
        ...(sessionId != null ? { session_id: sessionId } : {}),
      });
      setSent(true);
      setTimeout(onClose, 1400);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[rgba(36,28,18,0.45)] backdrop-blur-[2px] z-[400] flex items-center justify-center animate-fade-in"
      onClick={onClose}
    >
      <div className="card max-w-[440px] w-[90vw] px-7 py-7 animate-scale-in" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="text-center py-6">
            <div className="text-3xl mb-3">🙏</div>
            <h3 className="mb-1.5">Grazie!</h3>
            <p className="text-text-secondary text-sm">Your feedback is on its way.</p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-1">
              <h3>Send feedback</h3>
              <button
                className="bg-transparent border-none text-text-tertiary text-xl cursor-pointer leading-none transition-colors duration-200 hover:text-ink p-1 -mr-1 -mt-1"
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="text-text-tertiary text-[0.8125rem] leading-relaxed mb-4">
              {sessionId != null
                ? "Tell us what was off with this session — we read every report."
                : "Found a bug, have an idea, or something unclear? We read everything."}
            </p>

            <div className="flex gap-2 mb-3">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCategory(c.value)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors duration-150 cursor-pointer border ${
                    category === c.value
                      ? "border-warning bg-warning/15 text-warning"
                      : "border-border text-text-tertiary hover:text-ink hover:border-border-soft bg-transparent"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <textarea
              className="input-base w-full resize-none focus:outline-none text-base sm:text-sm"
              rows={4}
              autoFocus
              maxLength={4000}
              placeholder={
                category === "bug"
                  ? "What happened? What did you expect instead?"
                  : category === "idea"
                  ? "What would make Elena better for you?"
                  : "What can we help with?"
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />

            {error && <p className="text-error text-xs mt-2">{error}</p>}

            <div className="flex justify-end gap-3 mt-4">
              <button className="btn-quiet text-sm" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn-primary text-sm px-5 disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={submit}
                disabled={!message.trim() || submitting}
              >
                {submitting ? "Sending…" : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
